import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import type {
  ConfigurationOperation,
  PlaybookOperation,
} from "@repo/app-types/entities/configurationAgent";
import {
  collectPlaybookCriteria,
  collectPlaybookSections,
  type OutcomeDefinition,
  type PlaybookNodeId,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import {
  isKnowledgeBaseDocumentOperation,
  isLearningJourneyOperation,
  isSignalOperation,
  isTestCaseOperation,
} from "@repo/app-types/helpers/configurationAgentParser";
import {
  findCriteria,
  findSection,
} from "@repo/app-types/helpers/playbookTreeOperations";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { Size } from "@repo/ui/helpers/helpers";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import * as Sentry from "@sentry/react";
import { IconSparkles } from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { isOutcomeInSuggestionMode } from "../../../components/recordingAssessmentEditor/outcomeSettingsForm.js";
import {
  isCriterionSampleToolResultEntry,
  isOutcomeSampleToolResultEntry,
  isSectionSampleToolResultEntry,
  type UseSampleToolResultsResult,
} from "../../../hooks/useSampleToolResults.js";
import { ConfigurationAgentMessageContent } from "../configurationAssistant/configurationAgentMessageContent.js";
import {
  ConfigurationAssistantContext,
  type ConfigurationAssistantContextValue,
} from "../configurationAssistant/configurationAssistantContext.js";
import { PlaybookOperationCard } from "../configurationAssistant/playbookOperationCard.js";
import { buildInteractionResolverFromMessages } from "../interactionResolver.js";
import { useMastraChatContext } from "../mastra/mastraChatContext.js";
import { CountCustomerInteractionsTool } from "../tools/countCustomerInteractionsTool.js";
import { FallbackTool } from "../tools/fallbackTool.js";
import { GetCriterionDetailsTool } from "../tools/getCriterionDetailsTool.js";
import { GetKnowledgeDocumentTool } from "../tools/getKnowledgeDocumentTool.js";
import { GetSampleCriteriaScoreDistributionAndOutcomeCorrelationTool } from "../tools/getSampleCriteriaScoreDistributionAndOutcomeCorrelationTool.js";
import { GetSampleOutcomeDistributionTool } from "../tools/getSampleOutcomeDistributionTool.js";
import { GetSampleSectionApplicabilityTool } from "../tools/getSampleSectionApplicabilityTool.js";
import { GetSectionDetailsTool } from "../tools/getSectionDetailsTool.js";
import { ResearchCustomerInteractionsTool } from "../tools/researchCustomerInteractionsTool.js";
import { TestCriterionProposalTool } from "../tools/testCriterionProposalTool.js";
import {
  getPlaybookAssistantSuggestions,
  type PlaybookAssistantSuggestion,
} from "./playbookAssistantSuggestions.js";
import { usePlaybookPrefillFromUrl } from "./playbookConfigurationAssistantPrefill.js";
import {
  PlaybookDraftSuggestionList,
  type PlaybookDraftSuggestion,
  type PlaybookDraftSuggestionKind,
  type ViewedPlaybookNode,
} from "./playbookDraftSuggestionList.js";

export interface PlaybookConfigurationAssistantProps {
  playbookTree: PlaybookTree | undefined;
  outcomeDefinition: OutcomeDefinition | null | undefined;
  isKnowledgeBaseEnabled: boolean;
  onApplyOperation: (operation: PlaybookOperation) => Promise<void>;
  /**
   * Sample-tool results saved by the agent during this editing session.
   * Drives the "Playbook suggestions" list above the composer; the list lets
   * the user jump to a criterion or section that has an outstanding agent
   * suggestion. As suggestions are applied or discarded their entries leave
   * this map and the corresponding rows disappear.
   */
  sampleToolResults?: UseSampleToolResultsResult["results"];
  /** Called when the user clicks a row to navigate to that node. */
  onSelectDraftSuggestion?: (
    nodeId: PlaybookNodeId,
    kind: PlaybookDraftSuggestionKind,
  ) => void;
  /** The node currently selected in the editor, to flag the "Viewing" row. */
  viewedNode?: ViewedPlaybookNode | null;
}

const TOOL_COMPONENTS = {
  byName: {
    getCriterionDetailsTool: GetCriterionDetailsTool,
    getSectionDetailsTool: GetSectionDetailsTool,
    countCustomerInteractionsTool: CountCustomerInteractionsTool,
    researchCustomerInteractionsTool: ResearchCustomerInteractionsTool,
    getKnowledgeDocumentTool: GetKnowledgeDocumentTool,
    // Pre-rename key, kept so historical threads render.
    knowledgeRetrievalTool: GetKnowledgeDocumentTool,
    testCriterionProposalTool: TestCriterionProposalTool,
    getSampleCriteriaScoreDistributionAndOutcomeCorrelationTool:
      GetSampleCriteriaScoreDistributionAndOutcomeCorrelationTool,
    getSampleSectionApplicabilityTool: GetSampleSectionApplicabilityTool,
    getSampleOutcomeDistributionTool: GetSampleOutcomeDistributionTool,
  },
  Fallback: FallbackTool,
};

export const PlaybookConfigurationAssistant: FC<
  PlaybookConfigurationAssistantProps
> = (props) => {
  const {
    playbookTree,
    outcomeDefinition,
    isKnowledgeBaseEnabled,
    sampleToolResults,
    onSelectDraftSuggestion,
    viewedNode,
  } = props;
  const chat = useMastraChatContext();
  const [composerText, setComposerText] = useState("");
  const prefill = usePlaybookPrefillFromUrl();
  const hasAppliedPrefillRef = useRef(false);

  const draftSuggestions = useMemo<readonly PlaybookDraftSuggestion[]>(() => {
    if (!sampleToolResults) return [];
    const entries = Object.values(sampleToolResults).filter((entry) => {
      if (
        isCriterionSampleToolResultEntry(entry) ||
        isSectionSampleToolResultEntry(entry)
      ) {
        return true;
      }
      // The outcome has no node name, so only list it when it's an actual
      // applyable rubric suggestion (context differs) — a check-only run
      // would otherwise be a dead-end row (the Outcome pane renders the
      // suggestion UI only when the context differs).
      if (isOutcomeSampleToolResultEntry(entry)) {
        return (
          outcomeDefinition != null &&
          isOutcomeInSuggestionMode(outcomeDefinition, entry)
        );
      }
      return false;
    });
    if (entries.length === 0) return [];
    const nameById = new Map<string, string>();
    if (playbookTree) {
      for (const criterion of collectPlaybookCriteria(playbookTree)) {
        nameById.set(criterion.id, criterion.name);
      }
      for (const section of collectPlaybookSections(playbookTree)) {
        nameById.set(section.id, section.name);
      }
    }
    return entries.map((entry): PlaybookDraftSuggestion => {
      if (entry.kind === "outcome") {
        return {
          id: entry.playbook_node_id,
          name: "Outcome",
          kind: "outcome",
        };
      }
      const draftName = entry.candidate_draft.name;
      if (entry.kind === "new_criterion") {
        return {
          id: entry.playbook_node_id,
          name: draftName ?? "New criterion",
          kind: entry.kind,
        };
      }
      if (entry.kind === "new_section") {
        return {
          id: entry.playbook_node_id,
          name: draftName ?? "New section",
          kind: entry.kind,
        };
      }
      const fallback =
        entry.kind === "existing_section"
          ? "Untitled section"
          : "Untitled criterion";
      return {
        id: entry.playbook_node_id,
        name: nameById.get(entry.playbook_node_id) ?? draftName ?? fallback,
        kind: entry.kind,
      };
    });
  }, [sampleToolResults, playbookTree, outcomeDefinition]);

  const handleSuggestionSelect = useCallback(
    (suggestion: PlaybookDraftSuggestion) => {
      onSelectDraftSuggestion?.(suggestion.id, suggestion.kind);
    },
    [onSelectDraftSuggestion],
  );

  useEffect(() => {
    if (prefill && !hasAppliedPrefillRef.current) {
      hasAppliedPrefillRef.current = true;
      setComposerText(prefill.message);
    }
  }, [prefill]);

  const suggestions = useMemo(
    () =>
      getPlaybookAssistantSuggestions({
        playbookTree,
        outcomeDefinition,
        isKnowledgeBaseEnabled,
      }),
    [playbookTree, outcomeDefinition, isKnowledgeBaseEnabled],
  );

  // Track which operations have been applied using their stable IDs
  const [appliedOperationIds, setAppliedOperationIds] = useState(
    () => new Set<string>(),
  );

  const onApplyOperation = useEffectEvent(
    async (
      operationId: string,
      operation: ConfigurationOperation,
    ): Promise<void> => {
      // TODO: Refactor configuration editor context to handle this better
      if (
        isTestCaseOperation(operation) ||
        isKnowledgeBaseDocumentOperation(operation) ||
        isSignalOperation(operation) ||
        isLearningJourneyOperation(operation)
      ) {
        throw new Error("Operation type not supported by playbook editor");
      }

      // Check synchronously first to avoid applying twice
      if (appliedOperationIds.has(operationId)) {
        return; // Already applied, no-op
      }
      // Call side effect outside the state updater to avoid React violations
      await props.onApplyOperation(operation);
      // Update state after side effect
      setAppliedOperationIds((prev) => new Set(prev).add(operationId));
    },
  );

  const isOperationApplied = useCallback(
    (operationId: string) => appliedOperationIds.has(operationId),
    [appliedOperationIds],
  );

  const getEntityName = useCallback(
    (
      id: string,
      type: "section" | "criteria" | "test-case" | "chapter" | "lesson",
    ): string | undefined => {
      switch (type) {
        case "section":
          if (!playbookTree) {
            return undefined;
          }

          return findSection(playbookTree, id)?.name;

        case "criteria":
          if (!playbookTree) {
            return undefined;
          }

          return findCriteria(playbookTree, id)?.name;

        case "chapter":
        case "lesson":
        default:
          return undefined;
      }
    },
    [playbookTree],
  );

  const contextValue: ConfigurationAssistantContextValue = useMemo(
    () => ({
      applyOperation: onApplyOperation,
      isOperationApplied,
      getEntityName,
      getTestSpec: () => undefined,
      outcomeDefinition: outcomeDefinition ?? undefined,
      getDocumentDisplayName: () => undefined,
    }),
    [onApplyOperation, isOperationApplied, getEntityName, outcomeDefinition],
  );

  // Quotes and contact references in the agent's messages cite interactions
  // returned by its tool calls, so resolve them against those tool results.
  const resolveInteractionId = useMemo(
    () => buildInteractionResolverFromMessages(chat.messages),
    [chat.messages],
  );

  const lastMessageId = chat.messages.at(-1)?.id;

  return (
    <ConfigurationAssistantContext.Provider value={contextValue}>
      <ChatThread
        className="w-[350px] flex-auto"
        messages={chat.messages}
        isLoading={chat.isLoading}
        emptyState={<EmptyState suggestions={suggestions} />}
        renderMessage={(message) => (
          <Sentry.ErrorBoundary fallback={<AssistantMessageError />}>
            <ChatMessage
              message={message}
              isStreaming={chat.isRunning && message.id === lastMessageId}
              tools={TOOL_COMPONENTS}
              renderText={(args) =>
                renderPlaybookText(args, resolveInteractionId)
              }
            />
          </Sentry.ErrorBoundary>
        )}
        composer={
          <>
            {draftSuggestions.length > 0 ? (
              <div className="min-w-0 overflow-hidden px-3 pt-3 pb-2">
                <PlaybookDraftSuggestionList
                  suggestions={draftSuggestions}
                  onSelect={handleSuggestionSelect}
                  viewedNode={viewedNode}
                />
              </div>
            ) : null}

            <div className="border-t border-border-light bg-white p-3">
              <ChatComposer
                size={Size.SMALL}
                placeholder="Describe what you want to change..."
                value={composerText}
                onValueChange={setComposerText}
                isRunning={chat.isRunning}
                onStop={chat.stop}
                onSend={chat.sendMessage}
              />
            </div>
          </>
        }
      />
    </ConfigurationAssistantContext.Provider>
  );
};

/** Renders a playbook-agent text part, interleaving operation cards. */
function renderPlaybookText(
  { text, role, isStreaming }: ChatMessageRenderTextArgs,
  resolveInteractionId: InteractionResolver,
) {
  return (
    <ConfigurationAgentMessageContent
      text={text}
      role={role}
      isStreaming={isStreaming}
      resolveInteractionId={resolveInteractionId}
      renderOperation={(segment) => (
        <PlaybookOperationCard
          operationId={segment.id}
          operation={segment.operation}
        />
      )}
    />
  );
}

const EmptyState: FC<{
  suggestions: readonly PlaybookAssistantSuggestion[];
}> = ({ suggestions }) => {
  const chat = useMastraChatContext();
  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <div className="flex size-10 items-center justify-center rounded-full bg-indigo-100">
          <IconSparkles className="size-5 text-indigo-600" />
        </div>

        <h3 className="mt-3 text-sm font-semibold text-foreground">
          Set up your playbook with AI
        </h3>

        <p className="mt-1 max-w-[260px] text-xs text-muted-foreground">
          I can analyze calls and draft updates. All changes need your approval
          before going live.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 pb-0">
        {suggestions.map((suggestion) => (
          <SuggestionPill
            key={suggestion.label}
            suggestion={suggestion}
            onClick={() => chat.sendMessage(suggestion.prompt)}
          />
        ))}
      </div>
    </div>
  );
};

const SuggestionPill: FC<{
  suggestion: PlaybookAssistantSuggestion;
  onClick: () => void;
}> = ({ suggestion, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full border border-border-light bg-white px-3 py-1.5 text-left text-xs text-gray-700 shadow-xs transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
  >
    {suggestion.label}
  </button>
);

const AssistantMessageError: FC = () => (
  <div
    className={messageItemStyle({
      layout: "agent",
      role: "assistant",
    }).bubble({ className: "bg-red-100 px-3.5" })}
  >
    Unable to render this message.
  </div>
);
